import React, { useEffect } from 'react'

import { FontWeight, Text } from '@gousto-internal/citrus-react'

import { queueTrackingEvent } from '@library/tracking-integration'

import { OrderFeedbackText } from './OrderFeedbackText'
import { SelectedSentiment, SentimentOptions } from './SentimentOptions'

type OrderFeedbackEvent = {
  action: 'order_feedback'
  properties: {
    feedback_rating: string
    order_id: string
    event_screen: string
  }
}

type OrderFeedbackCompletedEvent = {
  action: 'order_feedback_completed'
  properties: {
    feedback_rating: string
    feedback_text: string
    order_id: string
    event_screen: string
  }
}

type OrderFeedbackFormProps = {
  onSubmit: (score?: number) => void
  orderId: string
}

export const OrderFeedbackForm = ({ onSubmit, orderId }: OrderFeedbackFormProps) => {
  const [selected, setSelected] = React.useState<SelectedSentiment | null>(null)
  const textareaRef = React.useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    if (selected) {
      textareaRef.current?.focus()
    }
  }, [selected])

  const handleSelect = ({ score, value }: SelectedSentiment) => {
    queueTrackingEvent<OrderFeedbackEvent>({
      action: 'order_feedback',
      properties: {
        feedback_rating: value,
        order_id: orderId,
        event_screen: 'view_order_confirmation_screen',
      },
    })
    setSelected({ score, value })
  }

  const handleSubmit = (feedback: string) => {
    queueTrackingEvent<OrderFeedbackCompletedEvent>({
      action: 'order_feedback_completed',
      properties: {
        feedback_rating: selected?.value ?? '',
        feedback_text: feedback,
        order_id: orderId,
        event_screen: 'view_order_confirmation_screen',
      },
    })
    onSubmit(selected?.score)
  }

  const isSentimentPositive = (selected?.score ?? 0) >= 3

  return (
    <>
      <Text fontWeight={FontWeight.Bold} size={5}>
        How was your experience choosing recipes?
      </Text>
      <SentimentOptions onSelect={handleSelect} selected={selected?.value} />
      {selected && (
        <OrderFeedbackText
          isSentimentPositive={isSentimentPositive}
          onSubmit={handleSubmit}
          textareaRef={textareaRef}
        />
      )}
    </>
  )
}
